import SearchBar, { SearchBarHelper } from 'features/searchBar';
import { tap, distinctUntilChanged, filter, map } from 'rxjs/operators';
import PageNavigationAction from 'enums/pageNavigationAction';
import { store, store$ } from '@redux/store';
import { searchParametersActions, selectLocationType, selectLocation, LocationType }
from 'features/searchResultsFacets';

import getRedirectAction from './searchRedirectHandler';

/**
 * Retrieves the location type from the Redux store.
 * @returns {string} The location type or empty string if not available.
 */
function getLocationType(state) {
  return selectLocationType(state) || '';
}

/**
 * Derives the initial search bar display text from the Redux store location.
 * This always reflects the current URL's search criteria, avoiding stale cookie values
 * that appear when a user navigates directly to a Search Results page via URL.
 * @returns {string} The formatted search text, or an empty string if unavailable.
 */
function getInitialSearchText(state) {
  const location = selectLocation(state);
  return SearchBarHelper.getSearchTextFromState(location);
}

/**
 * @returns {SearchBar} The initialized SearchBar instance.
 */
function createSearchBar() {
  const state = store.getState();
  const config = {
    searchContainers: document.querySelectorAll('[data-search-bar-container]'),
    smallScreenSearchContainer: document.querySelector('[data-search-bar-container-mobile]'),
    searchBarModalOpen: document.querySelector('[data-open-search-bar-modal]'),
    searchBarModal: document.querySelector('[data-search-bar-modal]'),
    header: document.querySelector('[data-main-header]'),
    savedSearchContainer: document.querySelector('[data-saved-search-container]'),
    locationType: getLocationType(state),
    initialSearchText: getInitialSearchText(state)
  };

  return new SearchBar(config);
}

/**
 * Dispatches an action to update the location in the Redux store.
 * @param {Object} payload - The payload containing location data.
 */
function dispatchUpdateLocation(payload) {
  store.dispatch(searchParametersActions.fetchLocations(payload));
}

/**
 * Performs a page redirect.
 * @param {Object} param0 - The redirect parameters.
 */
function performRedirect({ url, shouldOpenInNewTab }) {
  if (shouldOpenInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}

/**
 * Handles page refresh or redirect based on the action type.
 * @param {Object|null} data - Action descriptor from getRedirectAction.
 */
function pageRefreshOrRedirect(data) {
  if (!data) { return; }
  const action = getRedirectAction(data);
  if (action) {
    if (action.type === PageNavigationAction.updateLocation.name) {
      dispatchUpdateLocation(action.payload);
    } else if (action.type === PageNavigationAction.redirectToPage.name) {
      performRedirect(action);
    }
  }
}

/**
 * Attaches side effects to the SearchBar instance.
 * @param {SearchBar} searchBarInstance - The SearchBar instance.
 */
function observeSearchBarPageEvents(searchBarInstance) {
  searchBarInstance.getSelectedResultObservable(searchBarInstance)
    .pipe(
      tap((data) => pageRefreshOrRedirect(data))
    )
    .subscribe();
}

/**
 * Observes the change in location type and calls the appropriate handler if conditions are met.
 * @param {SearchBar} searchBarInstance - The SearchBar instance.
 */
function observeLocationTypeChanges(searchBarInstance) {
  store$.pipe(
    map(selectLocationType),
    distinctUntilChanged(),
    filter((location) => location === LocationType.RectangularArea.name),
  ).subscribe(() => searchBarInstance.handleLocationTypeChange(''));
}

/**
 * Initializes the SearchBar when DOM is ready.
 */
function initSearchBarOnReady() {
  const searchBarInstance = createSearchBar();
  observeSearchBarPageEvents(searchBarInstance);
  observeLocationTypeChanges(searchBarInstance);
}

/**
 * Initialize the search bar when the DOM is fully loaded.
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchBarOnReady);
} else {
  initSearchBarOnReady();
}
